import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Plus, CheckCircle, Circle, Trash2, Sparkles, RefreshCw, Phone, Mail, Calendar, FileText, Users, Building2, Paperclip, Upload, Download, X as XIcon, CreditCard, Check, AlertTriangle, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../utils/format';
import api from '../services/api';
import CreatePaymentPlanModal from '../components/CreatePaymentPlanModal';

const STAGE_CONFIG = {
  lead: { label: 'Lead', color: '#6366F1', bg: 'bg-[#6366F1]/10', text: 'text-[#6366F1]' },
  qualified: { label: 'Qualifié', color: '#3B82F6', bg: 'bg-[#3B82F6]/10', text: 'text-[#3B82F6]' },
  proposal: { label: 'Proposition', color: '#FFAB00', bg: 'bg-[#FFAB00]/10', text: 'text-[#B07800]' },
  negotiation: { label: 'Négociation', color: '#F97316', bg: 'bg-[#F97316]/10', text: 'text-[#F97316]' },
  won: { label: 'Gagné', color: '#1B99E5', bg: 'bg-[#1B99E5]/10', text: 'text-[#1B99E5]' },
  lost: { label: 'Perdu', color: '#EF4444', bg: 'bg-[#EF4444]/10', text: 'text-[#EF4444]' },
};

const ACTIVITY_ICONS = { call: Phone, email: Mail, task: CheckCircle, note: FileText };
const ACTIVITY_COLORS = {
  call: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  email: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  task: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  note: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};
const ACTIVITY_LABELS = { call: 'Appel', email: 'Email', task: 'Tâche', note: 'Note' };

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [stages, setStages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDeal, setShowEditDeal] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [dealForm, setDealForm] = useState({});
  const [activityForm, setActivityForm] = useState({ type: 'call', title: '', description: '', due_date: '' });
  const [saving, setSaving] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = React.useRef(null);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [showCreatePlan, setShowCreatePlan] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/deals/${id}`),
      api.get('/pipeline/stages'),
      api.get(`/activities?deal_id=${id}`),
      api.get(`/deals/${id}/documents`),
      api.get('/team')
    ]).then(([d, s, a, docs, t]) => {
      setDeal(d.data);
      setStages(s.data);
      setActivities(a.data);
      setDocuments(docs.data);
      setTeam(t.data);
      setDealForm({ ...d.data });
    }).catch(() => { toast.error('Devis introuvable'); navigate('/deals'); })
      .finally(() => setLoading(false));

    // Load payment plan (non-blocking)
    api.get(`/payment-plans/by-deal/${id}`)
      .then(r => setPaymentPlan(r.data))
      .catch(() => {});
  }, [id]);

  const handleUpdateDeal = async () => {
    setSaving(true);
    try {
          const res = await api.put(`/deals/${id}`, {
        title: dealForm.title, value: parseFloat(dealForm.value) || 0,
        currency: dealForm.currency, stage: dealForm.stage,
        contact_id: dealForm.contact_id, contact_name: dealForm.contact_name,
        company_id: dealForm.company_id, company_name: dealForm.company_name,
        probability: parseInt(dealForm.probability) || 0,
        expected_close_date: dealForm.expected_close_date, notes: dealForm.notes, tags: dealForm.tags || [],
        owner_id: dealForm.owner_id, owner_name: dealForm.owner_name
      });
      setDeal(res.data);
      setShowEditDeal(false);
      toast.success('Devis mis à jour');
    } catch { toast.error('Erreur lors de la mise à jour'); }
    finally { setSaving(false); }
  };

  const handleAddActivity = async () => {
    if (!activityForm.title) { toast.error('Le titre est requis'); return; }
    setSaving(true);
    try {
      const res = await api.post('/activities', {
        ...activityForm, deal_id: id, deal_title: deal?.title
      });
      setActivities(prev => [res.data, ...prev]);
      setShowAddActivity(false);
      setActivityForm({ type: 'call', title: '', description: '', due_date: '' });
      toast.success('Activité ajoutée');
    } catch { toast.error('Erreur lors de l\'ajout'); }
    finally { setSaving(false); }
  };

  const handleCompleteActivity = async (actId) => {
    try {
      await api.put(`/activities/${actId}/complete`);
      setActivities(prev => prev.map(a => a.id === actId ? { ...a, completed: true } : a));
      toast.success('Activité complétée');
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteActivity = async (actId) => {
    try {
      await api.delete(`/activities/${actId}`);
      setActivities(prev => prev.filter(a => a.id !== actId));
      toast.success('Activité supprimée');
    } catch { toast.error('Erreur'); }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingDoc(true);
    try {
      const res = await api.post(`/deals/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocuments(prev => [res.data, ...prev]);
      toast.success(`"${file.name}" ajouté`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (docId, docName) => {
    try {
      await api.delete(`/deals/${id}/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success(`"${docName}" supprimé`);
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleDownloadDocument = (docId, docName) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('crm_token');
    const link = document.createElement('a');
    link.href = `${API_URL}/api/deals/${id}/documents/${docId}/download`;
    link.setAttribute('download', docName);
    // Open in new tab with auth header won't work directly, use fetch instead
    fetch(`${API_URL}/api/deals/${id}/documents/${docId}/download`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }).catch(() => toast.error('Erreur téléchargement'));
  };

  const getAiSuggestion = async () => {    setAiLoading(true);
    try {
      const res = await api.post('/ai/deal-suggestion', { deal_id: id });
      setAiSuggestion(res.data.suggestion);
    } catch (err) {
      toast.error('Erreur IA: ' + (err.response?.data?.detail || 'Réessayez'));
    } finally { setAiLoading(false); }
  };

  const getAiSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await api.post('/ai/activity-summary', { deal_id: id });
      setAiSummary(res.data.summary);
    } catch (err) {
      toast.error('Erreur IA: ' + (err.response?.data?.detail || 'Réessayez'));
    } finally { setSummaryLoading(false); }
  };

  if (loading) return (
    <div className="p-6 flex justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B99E5]"></div>
    </div>
  );

  if (!deal) return null;
  const cfg = STAGE_CONFIG[deal.stage] || { label: deal.stage, bg: 'bg-gray-100', text: 'text-gray-600' };

  return (
    <div className="p-6">
      {/* Back + Header */}
      <div className="flex items-center gap-3 mb-5">
        <Button variant="ghost" size="sm" onClick={() => navigate('/deals')} className="h-8 w-8 p-0 rounded-lg">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{deal.title}</h1>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {deal.company_name && <span className="mr-3"><Building2 size={12} className="inline mr-1" />{deal.company_name}</span>}
            {deal.contact_name && <span><Users size={12} className="inline mr-1" />{deal.contact_name}</span>}
          </p>
        </div>
        <Button onClick={() => setShowEditDeal(true)} variant="outline" size="sm" className="h-8" data-testid="edit-deal-btn">
          <Edit2 size={13} className="mr-1.5" /> Modifier
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Left - main info */}
        <div className="lg:col-span-3 space-y-4">
          {/* Deal info card */}
          <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Informations</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Valeur', value: formatCurrency(deal.value) },
                { label: 'Probabilité', value: `${deal.probability || 0}%` },
                { label: 'Date de clôture', value: deal.expected_close_date || '—' },
                { label: 'Responsable', value: deal.owner_name || '—' },
              ].map(({ label, value }) => (                <div key={label}>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{label}</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{value}</p>
                </div>
              ))}
            </div>
            {deal.notes && (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Notes</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{deal.notes}</p>
              </div>
            )}
            {/* Progress bar */}
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-gray-500 dark:text-gray-400">Progression</span>
                <span className="text-xs font-semibold text-gray-900 dark:text-white">{deal.probability || 0}%</span>
              </div>
              <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${deal.probability || 0}%`, backgroundColor: cfg.color || '#1B99E5', transition: 'width 600ms ease' }} />
              </div>
            </div>
          </div>

          {/* Activities */}
          <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Activités ({activities.length})
              </h3>
              <Button onClick={() => setShowAddActivity(true)} size="sm" variant="outline" className="h-7 text-xs" data-testid="add-activity-btn">
                <Plus size={12} className="mr-1" /> Ajouter
              </Button>
            </div>
            {activities.length === 0 ? (
              <div className="py-8 text-center">
                <Calendar size={28} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-sm text-gray-400">Aucune activité</p>
                <Button onClick={() => setShowAddActivity(true)} size="sm" variant="ghost" className="mt-2 text-[#1B99E5]">
                  <Plus size={12} className="mr-1" /> Première activité
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {activities.map(act => {
                  const Icon = ACTIVITY_ICONS[act.type] || FileText;
                  return (
                    <div key={act.id} className={`flex items-start gap-3 p-3 rounded-lg border ${act.completed ? 'opacity-60 bg-gray-50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-700' : 'bg-white dark:bg-[#22262E] border-gray-100 dark:border-gray-700'}`} data-testid={`activity-${act.id}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${ACTIVITY_COLORS[act.type] || 'bg-gray-100 text-gray-500'}`}>
                        <Icon size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{ACTIVITY_LABELS[act.type] || act.type}</span>
                          {act.completed && <span className="text-xs text-[#1B99E5] font-medium">Complété</span>}
                        </div>
                        <p className={`text-sm font-medium mt-0.5 ${act.completed ? 'line-through text-gray-400' : 'text-gray-900 dark:text-white'}`}>{act.title}</p>
                        {act.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{act.description}</p>}
                        {act.due_date && <p className="text-xs text-gray-400 mt-0.5"><Calendar size={10} className="inline mr-1" />{act.due_date}</p>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!act.completed && (
                          <button onClick={() => handleCompleteActivity(act.id)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700" title="Marquer complété" data-testid={`complete-activity-${act.id}`}>
                            <Circle size={14} className="text-gray-300 hover:text-[#1B99E5]" style={{ transition: 'color 150ms ease' }} />
                          </button>
                        )}
                        <button onClick={() => handleDeleteActivity(act.id)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700" data-testid={`delete-activity-${act.id}`}>
                          <Trash2 size={14} className="text-gray-300 hover:text-red-500" style={{ transition: 'color 150ms ease' }} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Documents ({documents.length})
              </h3>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.docx,.doc,.txt,.xlsx,.png,.jpg,.jpeg"
                  onChange={handleUploadDocument}
                  data-testid="document-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  size="sm" variant="outline"
                  className="h-7 text-xs"
                  disabled={uploadingDoc}
                  data-testid="upload-document-btn"
                >
                  {uploadingDoc ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-[#1B99E5] mr-1" />
                  ) : (
                    <Upload size={12} className="mr-1" />
                  )}
                  {uploadingDoc ? 'Upload...' : 'Ajouter'}
                </Button>
              </div>
            </div>

            {documents.length === 0 ? (
              <div className="py-6 text-center">
                <Paperclip size={24} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-xs text-gray-400 mb-1">Aucun document attaché</p>
                <p className="text-xs text-gray-400">PDF, DOCX, TXT, images — max 10 Mo</p>
                <Button onClick={() => fileInputRef.current?.click()} size="sm" variant="ghost" className="mt-2 text-[#1B99E5] text-xs">
                  <Upload size={11} className="mr-1" /> Ajouter un document
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map(doc => {
                  const ext = doc.name?.split('.').pop()?.toLowerCase() || '';
                  const iconColors = { pdf: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400', docx: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400', doc: 'bg-blue-100 text-blue-600', txt: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400', xlsx: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' };
                  const sizeLabel = doc.size > 1024 * 1024 ? `${(doc.size / 1024 / 1024).toFixed(1)} Mo` : `${Math.round(doc.size / 1024)} Ko`;
                  return (
                    <div key={doc.id} className="flex items-center gap-2.5 p-2.5 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 group" data-testid={`document-${doc.id}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconColors[ext] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
                        <FileText size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{doc.name}</p>
                        <p className="text-xs text-gray-400">{sizeLabel} · {doc.uploaded_by}</p>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 150ms ease' }}>
                        <button
                          onClick={() => handleDownloadDocument(doc.id, doc.name)}
                          className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-[#1B99E5]"
                          style={{ transition: 'color 150ms ease' }}
                          title="Télécharger"
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <Download size={13} />
                        </button>
                        <button
                          onClick={() => handleDeleteDocument(doc.id, doc.name)}
                          className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-red-500"
                          style={{ transition: 'color 150ms ease' }}
                          title="Supprimer"
                          data-testid={`delete-doc-${doc.id}`}
                        >
                          <XIcon size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                <p className="text-xs text-gray-400 text-center pt-1">
                  Les documents sont analysés par l'IA pour améliorer les suggestions
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right - AI Panel */}
        <div className="lg:col-span-2 space-y-4">
          {/* AI Suggestions */}
          <div className="ai-panel bg-white dark:bg-[#1A1D23] rounded-xl border-2 border-transparent p-5" style={{ borderImage: 'linear-gradient(135deg, #1B99E5, #3B82F6) 1' }}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#1B99E5] to-[#3B82F6] flex items-center justify-center">
                <Sparkles size={14} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Suggestions IA</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Analyse intelligente du deal</p>
              </div>
            </div>

            {!aiSuggestion && !aiLoading && (
              <div className="py-4 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Obtenez des conseils personnalisés pour faire progresser ce deal
                </p>
                <Button
                  onClick={getAiSuggestion}
                  className="bg-gradient-to-r from-[#1B99E5] to-[#3B82F6] hover:opacity-90 text-white text-sm h-9 px-4 rounded-lg w-full"
                  style={{ transition: 'opacity 150ms ease' }}
                  data-testid="ai-suggestion-btn"
                >
                  <Sparkles size={14} className="mr-1.5" /> Analyser ce deal
                </Button>
              </div>
            )}

            {aiLoading && (
              <div className="py-6 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B99E5] mx-auto mb-2"></div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Analyse en cours...</p>
              </div>
            )}

            {aiSuggestion && !aiLoading && (
              <div>
                <div className="bg-gray-50 dark:bg-[#0F1115] rounded-lg p-3 text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto">
                  {aiSuggestion}
                </div>
                <button
                  onClick={getAiSuggestion}
                  className="mt-2 flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#1B99E5]"
                  style={{ transition: 'color 150ms ease' }}
                  data-testid="refresh-ai-btn"
                >
                  <RefreshCw size={11} /> Régénérer
                </button>
              </div>
            )}
          </div>

          {/* Activity Summary */}
          <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-[#6366F1]/10 flex items-center justify-center">
                <FileText size={14} className="text-[#6366F1]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Résumé IA</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Synthèse des activités</p>
              </div>
            </div>

            {!aiSummary && !summaryLoading && (
              <div className="py-3 text-center">
                <Button onClick={getAiSummary} variant="outline" size="sm" className="w-full text-xs h-8" data-testid="ai-summary-btn">
                  <FileText size={12} className="mr-1.5" /> Générer le résumé
                </Button>
              </div>
            )}

            {summaryLoading && (
              <div className="py-4 text-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#6366F1] mx-auto mb-1"></div>
                <p className="text-xs text-gray-400">Génération...</p>
              </div>
            )}

            {aiSummary && !summaryLoading && (
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed bg-gray-50 dark:bg-[#0F1115] rounded-lg p-3">
                  {aiSummary}
                </p>
                <button onClick={getAiSummary} className="mt-2 flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#6366F1]" style={{ transition: 'color 150ms ease' }}>
                  <RefreshCw size={11} /> Régénérer
                </button>
              </div>
            )}
          </div>

          {/* Payment Plan section */}
          <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-[#1B99E5]/10 flex items-center justify-center">
                <CreditCard size={14} className="text-[#1B99E5]" />
              </div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Plan de paiement</p>
            </div>

            {!paymentPlan && deal.stage === 'won' && (
              <div className="text-center py-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Aucun plan créé pour ce devis</p>
                <Button
                  onClick={() => setShowCreatePlan(true)}
                  className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white h-8 text-xs w-full"
                  data-testid="create-payment-plan-btn"
                >
                  <CreditCard size={12} className="mr-1.5" /> Créer l'échéancier
                </Button>
              </div>
            )}

            {!paymentPlan && deal.stage !== 'won' && (
              <div className="py-3 text-center">
                <AlertTriangle size={20} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-xs text-gray-400">Passez ce devis à <strong>Gagné</strong> pour créer un plan de paiement.</p>
              </div>
            )}

            {paymentPlan && (
              <div className="space-y-2.5">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                    <p className="text-xs text-gray-400">Valeur contrat</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">{formatCurrency(paymentPlan.total_contract_value)}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                    <p className="text-xs text-gray-400">Encaissé</p>
                    <p className="text-sm font-bold text-green-700 dark:text-green-400">{formatCurrency(paymentPlan.total_collected)}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                    <p className="text-xs text-gray-400">Reste à encaisser</p>
                    <p className="text-sm font-bold text-orange-600">{formatCurrency(paymentPlan.remaining)}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                    <p className="text-xs text-gray-400">Prochaine échéance</p>
                    <p className={`text-sm font-bold ${paymentPlan.has_late ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
                      {paymentPlan.next_due_date ? formatDate(paymentPlan.next_due_date) : '—'}
                    </p>
                  </div>
                </div>
                {paymentPlan.has_late && (
                  <div className="flex items-center gap-1.5 bg-red-50 dark:bg-red-900/20 rounded-lg px-2.5 py-1.5">
                    <AlertTriangle size={12} className="text-red-600 flex-shrink-0" />
                    <p className="text-xs text-red-700 dark:text-red-400">Des échéances sont en retard</p>
                  </div>
                )}
                <Button
                  variant="outline"
                  className="w-full h-7 text-xs border-[#1B99E5] text-[#1B99E5] hover:bg-[#1B99E5]/10"
                  onClick={() => navigate('/paiements')}
                  data-testid="view-payment-plan-btn"
                >
                  <ChevronRight size={12} className="mr-1" /> Voir le détail complet
                </Button>
              </div>
            )}
          </div>

          {/* Stage timeline */}
          <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Pipeline</h3>
            <div className="space-y-1.5">
              {stages.map(s => {
                const cfg2 = STAGE_CONFIG[s.id];
                const isActive = s.id === deal.stage;
                const isDone = stages.findIndex(st => st.id === deal.stage) > stages.findIndex(st => st.id === s.id);
                return (
                  <div key={s.id} className={`flex items-center gap-2.5 py-1.5 px-2.5 rounded-lg ${isActive ? 'bg-gray-50 dark:bg-gray-800/50' : ''}`}>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isDone ? 'bg-[#1B99E5]' : isActive ? '' : 'bg-gray-200 dark:bg-gray-700'}`}
                      style={{ backgroundColor: isActive ? (cfg2?.color || '#94a3b8') : undefined }} />
                    <span className={`text-xs ${isActive ? 'font-semibold text-gray-900 dark:text-white' : isDone ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-400 dark:text-gray-500'}`}>
                      {s.name}
                    </span>
                    {isActive && <span className="ml-auto text-[10px] font-bold text-white px-1.5 py-0.5 rounded-full" style={{ backgroundColor: cfg2?.color || '#94a3b8' }}>Actuel</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Deal Dialog */}
      <Dialog open={showEditDeal} onOpenChange={setShowEditDeal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Modifier le devis</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Titre</Label>
              <Input value={dealForm.title || ''} onChange={e => setDealForm({ ...dealForm, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Valeur (€)</Label>
                <Input type="number" value={dealForm.value || ''} onChange={e => setDealForm({ ...dealForm, value: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Probabilité (%)</Label>
                <Input type="number" min="0" max="100" value={dealForm.probability || ''} onChange={e => setDealForm({ ...dealForm, probability: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Étape</Label>
              <Select value={dealForm.stage} onValueChange={v => setDealForm({ ...dealForm, stage: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Date de clôture</Label>
              <Input type="date" value={dealForm.expected_close_date || ''} onChange={e => setDealForm({ ...dealForm, expected_close_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Notes</Label>
              <Textarea value={dealForm.notes || ''} onChange={e => setDealForm({ ...dealForm, notes: e.target.value })} rows={2} />
            </div>
            {team.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Responsable</Label>
                <Select
                  value={dealForm.owner_id || ''}
                  onValueChange={v => {
                    const member = team.find(m => m.id === v);
                    setDealForm({ ...dealForm, owner_id: v, owner_name: member?.name || '' });
                  }}
                >
                  <SelectTrigger data-testid="owner-select"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    {team.map(m => <SelectItem key={m.id} value={m.id}>{m.name} — {m.role}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDeal(false)}>Annuler</Button>
            <Button onClick={handleUpdateDeal} disabled={saving} className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white">
              {saving ? 'Sauvegarde...' : 'Mettre à jour'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Activity Dialog */}
      <Dialog open={showAddActivity} onOpenChange={setShowAddActivity}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Nouvelle activité</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Type</Label>
              <Select value={activityForm.type} onValueChange={v => setActivityForm({ ...activityForm, type: v })}>
                <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ACTIVITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Titre *</Label>
              <Input value={activityForm.title} onChange={e => setActivityForm({ ...activityForm, title: e.target.value })} placeholder="Ex: Appel de suivi" data-testid="activity-title-input" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Description</Label>
              <Textarea value={activityForm.description} onChange={e => setActivityForm({ ...activityForm, description: e.target.value })} rows={2} />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Date d'échéance</Label>
              <Input type="date" value={activityForm.due_date} onChange={e => setActivityForm({ ...activityForm, due_date: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddActivity(false)}>Annuler</Button>
            <Button onClick={handleAddActivity} disabled={saving} className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white" data-testid="save-activity-button">
              {saving ? 'Ajout...' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Payment Plan Modal */}
      {deal && (
        <CreatePaymentPlanModal
          deal={deal}
          open={showCreatePlan}
          onClose={() => setShowCreatePlan(false)}
          onCreated={(plan) => setPaymentPlan(plan)}
        />
      )}
    </div>
  );
}
